// src/components/circuit/geometry.js
// =============================================================================
// Helper geometrici condivisi per la gestione/creazione circuiti.
// Estratti da Circuits.jsx (per riuso fra "modifica circuito" ed editor manuale)
// + nuove utility per derivare la centerline da due bordi (editor manuale).
// =============================================================================

export const R_EARTH = 6371000;

export function toRad(d) { return (d * Math.PI) / 180; }
export function toDeg(r) { return (r * 180) / Math.PI; }

/* ===== Proiezione locale lat/lon → metri (per canvas) ===== */
export function projectLatLonToXY(points) {
    if (!points || points.length === 0) return [];
    const lat0 = points.reduce((s, p) => s + p.lat, 0) / points.length;
    const lon0 = points.reduce((s, p) => s + p.lon, 0) / points.length;
    const mPerDegLat = 111_132;
    const mPerDegLon = 111_320 * Math.cos((lat0 * Math.PI) / 180);
    return points.map(p => ({
        x: (p.lon - lon0) * mPerDegLon,
        y: (p.lat - lat0) * mPerDegLat * -1,
    }));
}

export function fitToCanvas(localXY, width, height, padding = 20) {
    if (!localXY.length) return [];
    const xs = localXY.map(p => p.x), ys = localXY.map(p => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const spanX = (maxX - minX) || 1, spanY = (maxY - minY) || 1;
    const scale = Math.min((width - 2 * padding) / spanX, (height - 2 * padding) / spanY);
    const offX = padding - minX * scale + (width - 2 * padding - spanX * scale) / 2;
    const offY = padding - minY * scale + (height - 2 * padding - spanY * scale) / 2;
    return localXY.map(p => ({ X: p.x * scale + offX, Y: p.y * scale + offY }));
}

export function haversineMeters(a, b) {
    if (!a || !b) return 0;
    const R = R_EARTH;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/* ===== Rotazione tracciato per nuovo start/finish ===== */
export function rotateTrack(points, startIdx, { preserveClosed = true } = {}) {
    if (!Array.isArray(points) || points.length < 2) return points || [];
    const isClosed = preserveClosed && points.length > 2 && haversineMeters(points[0], points[points.length - 1]) < 1;
    const core = isClosed ? points.slice(0, -1) : points.slice();
    const idx = Math.max(0, Math.min(core.length - 1, Number(startIdx) || 0));
    const rotated = [...core.slice(idx), ...core.slice(0, idx)];
    if (isClosed && rotated.length > 0) rotated.push({ ...rotated[0] });
    let dist = 0;
    return rotated.map((p, i) => {
        if (i > 0) dist += haversineMeters(rotated[i - 1], p);
        return { ...p, idx: i, dist };
    });
}

export function nearestTrackIndex(points, target) {
    if (!Array.isArray(points) || points.length === 0 || !target) return 0;
    let bestIdx = 0;
    let bestDist = Infinity;
    points.forEach((p, idx) => {
        const d = haversineMeters(p, target);
        if (d < bestDist) { bestDist = d; bestIdx = idx; }
    });
    return bestIdx;
}

export function startLineLatLngs(points, idx, widthMeters = 10) {
    if (!Array.isArray(points) || points.length < 2) return [];
    const center = points[idx] || points[0];
    const next = points[(idx + 1) % points.length] || points[idx + 1] || points[0];
    if (!center || !next) return [];
    const latRad = center.lat * Math.PI / 180;
    const metersPerDegLat = 111132;
    const metersPerDegLon = 111320 * Math.cos(latRad);
    const dx = (next.lon - center.lon) * metersPerDegLon;
    const dy = (next.lat - center.lat) * metersPerDegLat;
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    const half = Math.max(5, Number(widthMeters) || 10);
    const a = { lat: center.lat + (ny * half) / metersPerDegLat, lon: center.lon + (nx * half) / metersPerDegLon };
    const b = { lat: center.lat - (ny * half) / metersPerDegLat, lon: center.lon - (nx * half) / metersPerDegLon };
    return [[a.lat, a.lon], [b.lat, b.lon]];
}

/* ===========================================================================
   DERIVAZIONE CENTERLINE DA DUE BORDI (editor manuale)
   =========================================================================== */

/** Interpolazione lineare lat/lon (sufficiente alle scale di un circuito). */
export function lerpLatLon(a, b, t) {
    return { lat: a.lat + (b.lat - a.lat) * t, lon: a.lon + (b.lon - a.lon) * t };
}

/** Lunghezza totale di una polilinea (loop se closed). */
export function polylineLength(points, closed = false) {
    if (!Array.isArray(points) || points.length < 2) return 0;
    let d = 0;
    for (let i = 1; i < points.length; i++) d += haversineMeters(points[i - 1], points[i]);
    if (closed) d += haversineMeters(points[points.length - 1], points[0]);
    return d;
}

/** Area con segno (shoelace, in spazio proiettato locale): >0 / <0 = verso. */
export function signedArea(points) {
    const xy = projectLatLonToXY(points);
    let a = 0;
    for (let i = 0; i < xy.length; i++) {
        const p = xy[i];
        const q = xy[(i + 1) % xy.length];
        a += p.x * q.y - q.x * p.y;
    }
    return a / 2;
}

/** Ricampiona un loop chiuso a N punti equidistanti per frazione di arco. */
export function resampleClosed(points, N) {
    if (!Array.isArray(points) || points.length < 2) return [];
    const pts = points.slice();
    // assicura loop chiuso ai fini del campionamento
    const total = polylineLength(pts, true);
    if (total <= 0) return [];
    const out = [];
    const seg = total / N;
    let curIdx = 0;
    let accumulated = 0;
    let target = 0;
    const segLen = (i) => haversineMeters(pts[i], pts[(i + 1) % pts.length]);
    let curSegLen = segLen(0);
    for (let k = 0; k < N; k++) {
        while (accumulated + curSegLen < target && curIdx < pts.length) {
            accumulated += curSegLen;
            curIdx = (curIdx + 1) % pts.length;
            curSegLen = segLen(curIdx);
            if (curIdx === 0) break;
        }
        const localT = curSegLen > 0 ? (target - accumulated) / curSegLen : 0;
        const a = pts[curIdx];
        const b = pts[(curIdx + 1) % pts.length];
        out.push(lerpLatLon(a, b, Math.max(0, Math.min(1, localT))));
        target += seg;
    }
    return out;
}

/** Ricampiona una polilinea (aperta) a passo costante in metri → [{idx,lat,lon,dist}]. */
export function resampleBySpacing(points, spacingMeters = 1) {
    const out = [];
    if (!Array.isArray(points) || points.length < 2) return out;
    out.push({ idx: 0, lat: points[0].lat, lon: points[0].lon, dist: 0 });
    let traveled = 0;
    let nextDist = spacingMeters;
    for (let i = 1; i < points.length; i++) {
        const a = points[i - 1];
        const b = points[i];
        const segLen = haversineMeters(a, b);
        if (segLen <= 0) continue;
        while (traveled + segLen >= nextDist) {
            const t = (nextDist - traveled) / segLen;
            const p = lerpLatLon(a, b, t);
            out.push({ idx: Math.round(nextDist / spacingMeters), lat: p.lat, lon: p.lon, dist: nextDist });
            nextDist += spacingMeters;
        }
        traveled += segLen;
    }
    return out;
}

/** Punto più vicino su un SEGMENTO [a,b] (lat/lon) rispetto a p, + distanza (m). */
export function closestPointOnSegmentLL(p, a, b) {
    const mPerLat = 111320;
    const mPerLon = 111320 * Math.cos(p.lat * Math.PI / 180);
    const ax = (a.lon - p.lon) * mPerLon, ay = (a.lat - p.lat) * mPerLat;
    const bx = (b.lon - p.lon) * mPerLon, by = (b.lat - p.lat) * mPerLat;
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    let t = len2 > 0 ? -((ax * dx + ay * dy) / len2) : 0;
    t = Math.max(0, Math.min(1, t));
    const qx = ax + t * dx, qy = ay + t * dy;
    return {
        point: { lat: p.lat + qy / mPerLat, lon: p.lon + qx / mPerLon },
        dist: Math.hypot(qx, qy),
    };
}

/** Punto più vicino su una polilinea (di default chiusa) rispetto a p. */
export function closestPointOnPolyline(p, poly, closed = true) {
    if (!Array.isArray(poly) || poly.length < 2) return { dist: Infinity, point: p };
    let best = { dist: Infinity, point: poly[0] };
    const n = poly.length;
    const end = closed ? n : n - 1;
    for (let i = 0; i < end; i++) {
        const a = poly[i], b = poly[(i + 1) % n];
        const r = closestPointOnSegmentLL(p, a, b);
        if (r.dist < best.dist) best = r;
    }
    return best;
}

/**
 * Deriva la centerline (linea MEDIALE) da due bordi chiusi.
 * Per ogni punto del bordo sinistro trova il punto più vicino sul bordo destro
 * (proiezione perpendicolare al segmento): il punto medio resta sempre centrato
 * nel tracciato, anche con hairpin o larghezze molto diverse fra i bordi — al
 * contrario dell'accoppiamento per frazione, che andava in deriva e usciva pista.
 * Ricampiona poi la centerline a `spacingMeters` per compatibilità col motore.
 *
 * @returns { sectors:[{idx,lat,lon,dist}], widthProfile:number[], lengthMeters, widthMeters }
 *          oppure null se input non valido.
 */
export function deriveCenterline(leftBorder, rightBorder, { samples = 800, spacingMeters = 1 } = {}) {
    if (!Array.isArray(leftBorder) || !Array.isArray(rightBorder)) return null;
    if (leftBorder.length < 3 || rightBorder.length < 3) return null;

    // Punti ordinati lungo il bordo sinistro (definiscono l'andamento del giro)
    const L = resampleClosed(leftBorder, samples);
    if (L.length < 3) return null;

    const midRaw = [];
    const halfWidthRaw = [];
    for (let i = 0; i < L.length; i++) {
        const Li = L[i];
        const { point: Qi, dist } = closestPointOnPolyline(Li, rightBorder, true);
        midRaw.push({ lat: (Li.lat + Qi.lat) / 2, lon: (Li.lon + Qi.lon) / 2 });
        halfWidthRaw.push(dist / 2);
    }

    // Ricampiona a passo costante (sectors per il motore)
    const sectors = resampleBySpacing([...midRaw, midRaw[0]], spacingMeters);
    if (sectors.length < 2) return null;

    const widthProfile = sectors.map((_, i) => {
        const f = i / sectors.length;
        const src = Math.min(halfWidthRaw.length - 1, Math.floor(f * halfWidthRaw.length));
        return +(halfWidthRaw[src] || 0).toFixed(2);
    });

    const lengthMeters = sectors[sectors.length - 1].dist || polylineLength(midRaw, true);
    const avgHalf = halfWidthRaw.reduce((s, v) => s + v, 0) / (halfWidthRaw.length || 1);
    const widthMeters = +(avgHalf * 2).toFixed(1);

    return { sectors, widthProfile, lengthMeters, widthMeters };
}

/** True se l'ultimo punto è entro `thresholdM` dal primo (per auto-chiusura). */
export function shouldAutoClose(points, thresholdM = 12) {
    if (!Array.isArray(points) || points.length < 4) return false;
    return haversineMeters(points[0], points[points.length - 1]) <= thresholdM;
}

/**
 * Chaikin smoothing su un loop CHIUSO (i punti formano un anello, l'ultimo è
 * connesso al primo). Arrotonda i vertici angolosi del tracciato disegnato a
 * mano: i tratti rettilinei (punti ~collineari) restano invariati, solo gli
 * spigoli vengono smussati. Ogni iterazione raddoppia il numero di punti.
 */
export function chaikinClosed(points, iterations = 2) {
    if (!Array.isArray(points) || points.length < 3) return points || [];
    let p = points.map(pt => ({ lat: pt.lat, lon: pt.lon }));
    for (let k = 0; k < iterations; k++) {
        const n = p.length;
        if (n < 3) break;
        const out = [];
        for (let i = 0; i < n; i++) {
            const a = p[i], b = p[(i + 1) % n];
            out.push({ lat: a.lat * 0.75 + b.lat * 0.25, lon: a.lon * 0.75 + b.lon * 0.25 });
            out.push({ lat: a.lat * 0.25 + b.lat * 0.75, lon: a.lon * 0.25 + b.lon * 0.75 });
        }
        p = out;
    }
    return p;
}
